// Agent Memory API
// LazySEM Frontend — manage what the agent remembers about you (Phase 1.x).
// Routes through the gateway: /api/v1/agent/memory/*
//
// Auth model:
//   - In production the gateway injects X-User-ID and the backend reads it
//     from the request header (verified by the HMAC signature middleware).
//   - In development the request hits the agent service directly (no
//     gateway), so we forward the authenticated user's id as a ?user_id=
//     query param. The backend's _resolve_user_id() accepts either source.
//   - Callers obtain the id from AuthContext (`useAuth().user?.id`) and pass
//     it through; this matches the pattern already used by useAgent.ts for
//     the WebSocket payload (user_id field). We never hardcode a default.

import { authenticatedFetch } from './authenticatedFetch';

const MEMORY_PREFIX = '/api/v1/agent/memory';

function withUserId(path: string, userId?: string | null): string {
  if (!userId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}user_id=${encodeURIComponent(userId)}`;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MemoryEntry {
  id: string;
  memory: string;
  metadata?: Record<string, unknown> | null;
}

export interface MemoryListResponse {
  user_id: string;
  enabled: boolean;
  init_error?: string | null;
  memories: MemoryEntry[];
  total: number;
}

export interface MemoryRecallResult {
  id: string;
  memory: string;
  score?: number;
}

export interface MemoryRecallResponse {
  user_id: string;
  query: string;
  enabled: boolean;
  results: MemoryRecallResult[];
  total: number;
}

export interface MemoryDeleteResponse {
  deleted: boolean;
  memory_id?: string;
  reason?: string;
}

// ─── API ────────────────────────────────────────────────────────────────────

/**
 * List memories the agent has stored about the current user.
 * `limit` is clamped server-side (1–500).
 * `userId` is appended as ?user_id= when the gateway is not in front of us
 * (dev mode); ignored when omitted.
 */
export async function listMemories(
  limit = 50,
  userId?: string | null,
): Promise<MemoryListResponse> {
  const path = withUserId(
    `${MEMORY_PREFIX}?limit=${encodeURIComponent(String(limit))}`,
    userId,
  );
  return authenticatedFetch<MemoryListResponse>(path, { serviceName: 'Agent Memory' });
}

/**
 * Delete a single memory by id.
 */
export async function deleteMemory(
  memoryId: string,
  userId?: string | null,
): Promise<MemoryDeleteResponse> {
  const path = withUserId(
    `${MEMORY_PREFIX}/${encodeURIComponent(memoryId)}`,
    userId,
  );
  return authenticatedFetch<MemoryDeleteResponse>(path, {
    method: 'DELETE',
    serviceName: 'Agent Memory',
  });
}

/**
 * Search memories by free-text query (semantic similarity via Mem0).
 */
export async function recallMemories(
  query: string,
  topK = 5,
  userId?: string | null,
): Promise<MemoryRecallResponse> {
  const path = withUserId(`${MEMORY_PREFIX}/recall`, userId);
  return authenticatedFetch<MemoryRecallResponse>(path, {
    method: 'POST',
    body: JSON.stringify({ query, top_k: topK }),
    serviceName: 'Agent Memory',
  });
}
